import type { NormPoint } from "./scan-crop-math";

export type ScanFilterId = "none" | "document" | "grayscale" | "contrast" | "bw";

export type StoredScanPage = {
  id: string;
  originalDataUrl: string;
  croppedUrl: string | null;
  processedUrl: string | null;
  corners: [NormPoint, NormPoint, NormPoint, NormPoint];
  filter: ScanFilterId;
};

export type StoredScanDocument = {
  id: string;
  title: string;
  pages: StoredScanPage[];
  createdAt: string;
  updatedAt: string;
  /** Set when last saved to Nevisign cloud */
  nevisignContractId?: string;
  thumbnailUrl?: string | null;
};

const DB_NAME = "nevisign.reader.scan-library";
const DB_VERSION = 1;
const STORE = "scans";
const MAX_DOCS = 40;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB در دسترس نیست"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("updatedAt", "updatedAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () =>
      reject(req.error ?? new Error("باز کردن پایگاه اسکن ناموفق"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("تراکنش اسکن ناموفق"));
    tx.onabort = () => reject(tx.error ?? new Error("تراکنش اسکن لغو شد"));
  });
}

export function newScanDocId(): string {
  return `scan_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function thumbnailFromPages(pages: StoredScanPage[]): string | null {
  for (const p of pages) {
    if (p.processedUrl) return p.processedUrl;
    if (p.croppedUrl) return p.croppedUrl;
  }
  return pages[0]?.originalDataUrl ?? null;
}

export async function listScannedDocuments(): Promise<StoredScanDocument[]> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.getAll();
    const rows = await new Promise<StoredScanDocument[]>((resolve, reject) => {
      req.onsuccess = () =>
        resolve(Array.isArray(req.result) ? (req.result as StoredScanDocument[]) : []);
      req.onerror = () => reject(req.error);
    });
    await txDone(tx);
    db.close();
    return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch {
    return [];
  }
}

export async function getScannedDocument(
  id: string,
): Promise<StoredScanDocument | null> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readonly");
    const store = tx.objectStore(STORE);
    const req = store.get(id);
    const row = await new Promise<StoredScanDocument | null>((resolve, reject) => {
      req.onsuccess = () =>
        resolve((req.result as StoredScanDocument | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
    await txDone(tx);
    db.close();
    return row;
  } catch {
    return null;
  }
}

export async function upsertScannedDocument(
  doc: Omit<StoredScanDocument, "createdAt" | "updatedAt" | "thumbnailUrl"> & {
    createdAt?: string;
    updatedAt?: string;
    thumbnailUrl?: string | null;
    nevisignContractId?: string;
  },
): Promise<StoredScanDocument> {
  const now = new Date().toISOString();
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);

  const existing = await new Promise<StoredScanDocument | null>((resolve, reject) => {
    const req = store.get(doc.id);
    req.onsuccess = () =>
      resolve((req.result as StoredScanDocument | undefined) ?? null);
    req.onerror = () => reject(req.error);
  });

  const next: StoredScanDocument = {
    id: doc.id,
    title: doc.title.trim() || "اسکن بدون عنوان",
    pages: doc.pages,
    createdAt: doc.createdAt ?? existing?.createdAt ?? now,
    updatedAt: doc.updatedAt ?? now,
    nevisignContractId:
      doc.nevisignContractId ?? existing?.nevisignContractId,
    thumbnailUrl:
      doc.thumbnailUrl !== undefined
        ? doc.thumbnailUrl
        : thumbnailFromPages(doc.pages),
  };

  store.put(next);

  const all = await new Promise<StoredScanDocument[]>((resolve, reject) => {
    const allReq = store.getAll();
    allReq.onsuccess = () =>
      resolve(
        Array.isArray(allReq.result)
          ? (allReq.result as StoredScanDocument[])
          : [],
      );
    allReq.onerror = () => reject(allReq.error);
  });
  if (all.length > MAX_DOCS) {
    const sorted = [...all].sort((a, b) => a.updatedAt.localeCompare(b.updatedAt));
    const drop = sorted.slice(0, all.length - MAX_DOCS);
    for (const d of drop) {
      if (d.id !== next.id) store.delete(d.id);
    }
  }

  await txDone(tx);
  db.close();
  return next;
}

export async function removeScannedDocument(id: string): Promise<void> {
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    await txDone(tx);
    db.close();
  } catch {
    /* ignore */
  }
}

/** Lightweight list row without full page bitmaps (still includes thumbnail). */
export type ScannedDocSummary = {
  id: string;
  title: string;
  updatedAt: string;
  pageCount: number;
  thumbnailUrl?: string | null;
  nevisignContractId?: string;
};

export async function listScannedDocumentSummaries(): Promise<ScannedDocSummary[]> {
  const docs = await listScannedDocuments();
  return docs.map((d) => ({
    id: d.id,
    title: d.title,
    updatedAt: d.updatedAt,
    pageCount: d.pages.length,
    thumbnailUrl: d.thumbnailUrl ?? thumbnailFromPages(d.pages),
    nevisignContractId: d.nevisignContractId,
  }));
}
